import React, { useEffect, useState } from "react";
import { Table, Form, Card, Row, Col, Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import "../../../../style/addrestoration.css";
import "../../../../style/addmaterial.css";
import { apiAxios } from "../../../../utils/apiAxios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface OrderType {
  orderTypeID: number;
  orderTypeName: string;
  turnaroundTimeHours: number;
  turnaroundTimeMinutes: number;
  maximumCapacity: string;
  acceptanceTiming: string;
  isActive: boolean;
}

const AddOrderType: React.FC = () => {
  const [orderTypeName, setOrderTypeName] = useState("");
  const [turnaroundTimeInput, setTurnaroundTimeInput] = useState<string>("");
  const [maximumCapacity, setMaximumCapacity] = useState("");
  const [acceptanceTiming, setAcceptanceTiming] = useState("");
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  
  const [editId, setEditId] = useState<number | null>(null);
  const [editOrderTypeName, setEditOrderTypeName] = useState("");
  const [editTurnaroundTimeInput, setEditTurnaroundTimeInput] = useState<string>("");
  const [editMaximumCapacity, setEditMaximumCapacity] = useState("");
  const [editAcceptanceTiming, setEditAcceptanceTiming] = useState("");
  const [editActive, setEditActive] = useState(true);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Function to check if input is "on demand" or similar
  const isOnDemandInput = (input: string): boolean => {
    if (!input || !input.trim()) return false;
    
    const lowerInput = input.toLowerCase().trim();
    const onDemandKeywords = ['on demand', 'ondemand', 'on-demand', 'custom', 'as per requirement', 'as required'];
    
    return onDemandKeywords.some(keyword => lowerInput.includes(keyword));
  };

  // Function to parse turnaround time string into hours and minutes
  const parseTurnaroundTime = (input: string): { hours: number; minutes: number; isOnDemand: boolean } => {
    if (!input || !input.trim()) return { hours: 0, minutes: 0, isOnDemand: false };
    
    const lowerInput = input.toLowerCase().trim();
    
    // Check if it's "on demand"
    if (isOnDemandInput(lowerInput)) {
      return { hours: 0, minutes: 0, isOnDemand: true };
    }
    
    // Check for hours and minutes separately
    const hourRegex = /(\d+(?:\.\d+)?)\s*(?:hr|hrs|hour|hours|h)/i;
    const minRegex = /(\d+)\s*(?:min|mins|minute|minutes|m)/i;
    
    // Check for decimal hours like "1.5 hours"
    const decimalHourMatch = lowerInput.match(/(\d+(?:\.\d+)?)\s*(?:hr|hrs|hour|hours|h)/i);
    
    // Check for simple number (assume hours)
    const simpleNumberMatch = lowerInput.match(/^\s*(\d+(?:\.\d+)?)\s*$/);
    
    let hours = 0;
    let minutes = 0;
    
    if (hourRegex.test(lowerInput) || minRegex.test(lowerInput)) {
      // Has explicit hour or minute indicators
      if (hourRegex.test(lowerInput)) {
        const hourMatch = lowerInput.match(hourRegex);
        if (hourMatch) {
          hours = parseFloat(hourMatch[1]);
        }
      }
      
      if (minRegex.test(lowerInput)) {
        const minMatch = lowerInput.match(minRegex);
        if (minMatch) {
          minutes = parseInt(minMatch[1]);
        }
      }
    } else if (decimalHourMatch) {
      // Decimal hours like "1.5 hr"
      hours = parseFloat(decimalHourMatch[1]);
    } else if (simpleNumberMatch) {
      // Just a number, assume hours
      hours = parseFloat(simpleNumberMatch[1]);
    } else {
      // Try to extract any numbers
      const numbers = lowerInput.match(/\d+(?:\.\d+)?/g);
      if (numbers && numbers.length > 0) {
        hours = parseFloat(numbers[0]);
      }
    }
    
    // Handle decimal hours (convert .5 to 30 minutes, etc.)
    const decimalPart = hours % 1;
    if (decimalPart > 0) {
      minutes = Math.round(decimalPart * 60);
      hours = Math.floor(hours);
    }
    
    // Ensure minutes are 0-59
    if (minutes >= 60) {
      hours += Math.floor(minutes / 60);
      minutes = minutes % 60;
    }
    
    return { hours, minutes, isOnDemand: false };
  };

  // Function to format hours and minutes back to display string
  const formatTurnaroundTime = (hours: number, minutes: number, inputText?: string): string => {
    // If more than 60 hours, show as "On Demand"
    if (hours > 60) {
      return "On Demand";
    }
    
    if (hours === 0 && minutes === 0) {
      // Check if original input was "on demand"
      if (inputText && isOnDemandInput(inputText)) {
        return "On Demand";
      }
      return "";
    }
    
    let result = "";
    
    if (hours > 0) {
      result += `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
    }
    
    if (minutes > 0) {
      if (result) result += " ";
      result += `${minutes} ${minutes === 1 ? 'min' : 'mins'}`;
    }
    
    return result;
  };

const fetchOrderTypes = async () => {
  setFetching(true);
  try {
    const { data } = await apiAxios.get(`/OrderTypeMaster/GetAll`);

    if (data?.isSuccess) {
      const orderTypesData = data.data || data.singleData || [];

      const transformedData = orderTypesData.map((item: any) => {
        const hours = Number(item.turnaroundTimeValue || 0);
        const minutes = Number(item.turnaroundTimeUnit || 0);

        return {
          ...item,
          turnaroundTimeHours: hours,
          turnaroundTimeMinutes: minutes,
        };
      });

      setOrderTypes([...transformedData].reverse());
    } else {
      toast.error(data?.status || "Failed to fetch order types");
      setOrderTypes([]);
    }
  } catch (err: any) {
    console.warn(err);
    toast.error(err?.response?.data?.status || "Error fetching order types");
    setOrderTypes([]);
  } finally {
    setFetching(false);
  }
};

  useEffect(() => {
    fetchOrderTypes();
  }, []);

 const handleAdd = async () => {
  if (loading) return;

  const trimmedName = orderTypeName.trim();
  if (!trimmedName) {
    toast.warning("Please enter Order Type Name!");
    return;
  }

  const { hours, minutes, isOnDemand } = parseTurnaroundTime(turnaroundTimeInput);

  if (minutes < 0 || minutes >= 60) {
    toast.warning("Minutes should be between 0 and 59");
    return;
  }

  if (!isOnDemand && hours < 0) {
    toast.warning("Hours cannot be negative");
    return;
  }

  setLoading(true);

  try {
    const finalHours = isOnDemand ? 61 : hours;
    const finalMinutes = isOnDemand ? 0 : minutes;

    const payload = {
      orderTypeID: 0,
      orderTypeName: trimmedName,
      turnaroundTimeValue: finalHours,
      turnaroundTimeUnit: finalMinutes,
      maximumCapacity: maximumCapacity || "",
      acceptanceTiming: acceptanceTiming || "",
      isActive: true,
    };

    const { data } = await apiAxios.post(`/OrderTypeMaster/Create`, payload);

    if (data?.isSuccess) {
      const newOrderType = {
        ...data.singleData,
        turnaroundTimeHours: finalHours,
        turnaroundTimeMinutes: finalMinutes,
      };

      setOrderTypes([newOrderType, ...orderTypes]);
      toast.success("Order type created successfully!");
      resetForm();
    } else {
      toast.error(data?.status || "Error saving order type");
    }
  } catch (err: any) {
    console.warn(err);
    toast.error(err?.response?.data?.status || "Something went wrong");
  } finally {
    setLoading(false);
  }
};

  const resetForm = () => {
    setOrderTypeName("");
    setTurnaroundTimeInput("");
    setMaximumCapacity("");
    setAcceptanceTiming("");
  };

  const handleEdit = (orderType: OrderType) => {
    setEditId(orderType.orderTypeID);
    setEditOrderTypeName(orderType.orderTypeName);
    
    // Format the stored hours/minutes back to display string
    let displayString = "";
    if (orderType.turnaroundTimeHours > 60) {
      displayString = "On Demand";
    } else if (orderType.turnaroundTimeHours === 0 && orderType.turnaroundTimeMinutes === 0) {
      displayString = "";
    } else {
      displayString = `${orderType.turnaroundTimeHours} ${orderType.turnaroundTimeHours === 1 ? 'hr' : 'hrs'}`;
      if (orderType.turnaroundTimeMinutes > 0) {
        displayString += ` ${orderType.turnaroundTimeMinutes} ${orderType.turnaroundTimeMinutes === 1 ? 'min' : 'mins'}`;
      }
    }
    
    setEditTurnaroundTimeInput(displayString);
    setEditMaximumCapacity(orderType.maximumCapacity);
    setEditAcceptanceTiming(orderType.acceptanceTiming);
    setEditActive(orderType.isActive);
  };

const handleUpdate = async () => {
  if (updating) return;

  if (!editOrderTypeName.trim() || editId === null) {
    toast.warning("Please enter order type name");
    return;
  }

  const { hours, minutes, isOnDemand } = parseTurnaroundTime(editTurnaroundTimeInput);

  if (minutes < 0 || minutes >= 60) {
    toast.warning("Minutes should be between 0 and 59");
    return;
  }

  if (!isOnDemand && hours < 0) {
    toast.warning("Hours cannot be negative");
    return;
  }

  setUpdating(true);

  try {
    const finalHours = isOnDemand || hours > 60 ? 61 : hours;
    const finalMinutes = isOnDemand || hours > 60 ? 0 : minutes;

    const payload = {
      orderTypeID: editId,
      orderTypeName: editOrderTypeName.trim(),
      turnaroundTimeValue: finalHours,
      turnaroundTimeUnit: finalMinutes,
      maximumCapacity: editMaximumCapacity || "",
      acceptanceTiming: editAcceptanceTiming || "",
      isActive: editActive,
      updatedByID: 1,
      updatedDate: new Date().toISOString(),
    };

    const { data } = await apiAxios.put(`/OrderTypeMaster/Update/${editId}`, payload);

    if (data?.isSuccess) {
      setOrderTypes(
        orderTypes.map((item) =>
          item.orderTypeID === editId
            ? {
                ...item,
                orderTypeName: editOrderTypeName.trim(),
                turnaroundTimeHours: finalHours,
                turnaroundTimeMinutes: finalMinutes,
              }
            : item
        )
      );

      toast.success("Order type updated successfully!");
      cancelEdit();
    } else {
      toast.error(data?.status || "Error updating order type");
    }
  } catch (err: any) {
    console.warn(err);
    toast.error(err?.response?.data?.status || "Something went wrong");
  } finally {
    setUpdating(false);
  }
};

  const cancelEdit = () => {
    setEditId(null);
    setEditOrderTypeName("");
    setEditTurnaroundTimeInput("");
    setEditMaximumCapacity("");
    setEditAcceptanceTiming("");
    setEditActive(true);
  };

const toggleStatus = async (id: number, currentStatus: boolean) => {
  try {
    const orderType = orderTypes.find(item => item.orderTypeID === id);
    if (!orderType) return;

    const updatedOrderType = { ...orderType, isActive: !currentStatus };

    const { data } = await apiAxios.put(`/OrderTypeMaster/Update`, updatedOrderType);

    if (data?.isSuccess) {
      setOrderTypes(
        orderTypes.map((item) =>
          item.orderTypeID === id
            ? { ...item, isActive: !currentStatus }
            : item
        )
      );

      toast.success("Status updated successfully!");
    } else {
      toast.error(data?.status || "Error updating status");
    }
  } catch (err: any) {
    console.warn(err);
    toast.error(err?.response?.data?.status || "Something went wrong");
  }
};

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

const handleDelete = async () => {
  if (deleteId === null) return;

  try {
    const { data } = await apiAxios.delete(`/OrderTypeMaster/Delete/${deleteId}`);

    if (data?.isSuccess) {
      setOrderTypes(orderTypes.filter((item) => item.orderTypeID !== deleteId));
      toast.success("Order type deleted successfully!");
    } else {
      toast.error(data?.status || "Error deleting order type");
    }
  } catch (err: any) {
    console.warn(err);
    toast.error(err?.response?.data?.status || "Something went wrong");
  } finally {
    setShowDeleteModal(false);
    setDeleteId(null);
  }
};

  const handleUpdateClick = () => {
    if (!updating) {
      handleUpdate();
    }
  };

  const handleAddClick = () => {
    if (!loading) {
      handleAdd();
    }
  };

  const getButtonClass = () => {
    const isDisabled = editId ? updating : loading;
    return `me-2 ${isDisabled ? 'disabled-btn' : ''}`;
  };

  const getButtonStyles = () => {
    if (editId) {
      return updating ? { opacity: 0.6, cursor: 'not-allowed' } : {};
    } else {
      return loading ? { opacity: 0.6, cursor: 'not-allowed' } : {};
    }
  };

  return (
    <div className="mt-1">
      {/* <h4 className="mb-4">Order Type Master</h4> */}
       <h4 className="mb-4">
            {editId ? "Edit Order Type" : "Add New Order Type"}
          </h4>

     <div className="premium-card mt-3">
      
         
          <div className="row">
            <div className="col-md-12 col-lg-3 col-12 mt-1">
          
                <Form.Label>Order Type Name <span className="asterick-icon"> * </span></Form.Label>
                <Form.Control
                  type="text"
                  className="form-control"
                  placeholder="Enter order type name"
                  value={orderTypeName}
                  onChange={(e) => setOrderTypeName(e.target.value)}
                />
            
            </div>
            <div className="col-md-12 col-lg-3 col-12  mt-1">
          
                <Form.Label>Maximum Capacity</Form.Label>
                <Form.Control
                  type="text"
                    className="form-control"
                  placeholder="e.g., 100 units"
                  value={maximumCapacity}
                  onChange={(e) => setMaximumCapacity(e.target.value)}
                />
             
            </div>
             <div className="col-md-12 col-lg-3 col-12  mt-1">
        
                <Form.Label>Acceptance Timing</Form.Label>
                <Form.Control
                  type="text"
                    className="form-control"
                  placeholder="e.g., 9 AM - 6 PM"
                  value={acceptanceTiming}
                  onChange={(e) => setAcceptanceTiming(e.target.value)}
                />
           
            </div>
           <div className="col-md-12 col-lg-3 col-12  mt-1">
           
                <Form.Label>Turnaround Time</Form.Label>
                <Form.Control
                  type="text"
                    className="form-control"
                  placeholder="e.g., 58 hr, 2 hours 30 mins, 45 min, 1.5 hr, On Demand"
                  value={turnaroundTimeInput}
                  onChange={(e) => setTurnaroundTimeInput(e.target.value)}
                />
                <div className="text-muted enter-time-text">
                  Enter time in any format. For more than 60 hours or custom timing, enter "On Demand"
                </div>
            
            </div>
          </div>
          <Row>
            <div className="text-start col-md-12">
              <div 
                style={getButtonStyles()}
                className="d-inline-block"
              >
                    <button type="submit" className={`btn py-2 px-3 btn-primary ${getButtonClass()}`}
                   onClick={editId ? handleUpdateClick : handleAddClick}>
                  <i className={`bi ${editId ? "" : ""}`}></i>{" "}
                  {editId 
                    ? (updating ? "Updating..." : "Update")
                    : (loading ? "Saving..." : "Add Order Type")
                  }
                </button>
              </div>
              {editId && (
                   <button type="submit" className="btn py-2 px-3 btn-secondary" onClick={cancelEdit}>
                Cancel
                </button>
              )}
            </div>
          </Row>
        </div>
    

     <div className="premium-card mt-4">
  
        <h5 className="mb-3">Order Types List</h5>
          {fetching ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading order types...</p>
            </div>
          ) : (
            <Table bordered hover responsive className="align-middle addorder-type-table mb-0 custom-material-table">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "80px" }}>Order ID</th>
                  <th>Order Type</th>
                  <th>Turnaround Time</th>
                  <th>Maximum Capacity</th>
                  <th>Acceptance Timing</th>
                  <th style={{ width: "100px" }}>Status</th>
                  <th style={{ width: "250px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orderTypes.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No order types found. {!fetching && "Try adding one!"}
                    </td>
                  </tr>
                ) : (
                  orderTypes.map((item: any) => (
                    <tr key={item.orderTypeID}>
                      <td>{item.orderTypeID}</td>
                      <td>
                        {editId === item.orderTypeID ? (
                          <Form.Control
                            type="text"
                            value={editOrderTypeName}
                            onChange={(e) => setEditOrderTypeName(e.target.value)}
                          />
                        ) : (
                          <span className="fw-semibold">{item.orderTypeName}</span>
                        )}
                      </td>
                      <td>
                        {editId === item.orderTypeID ? (
                          <Form.Control
                            type="text"
                            placeholder="e.g., 58 hr, 2 hours 30 mins, On Demand"
                            value={editTurnaroundTimeInput}
                            onChange={(e) => setEditTurnaroundTimeInput(e.target.value)}
                          />
                        ) : (
                          <span>
                            {item.turnaroundTimeHours > 60 ? (
                              <span className="text-primary fw-bold">On Demand</span>
                            ) : item.turnaroundTimeHours === 0 && item.turnaroundTimeMinutes === 0 ? (
                              "-"
                            ) : (
                              <>
                                {item.turnaroundTimeHours > 0 && `${item.turnaroundTimeHours} hrs `}
                                {item.turnaroundTimeMinutes > 0 && `${item.turnaroundTimeMinutes} mins`}
                              </>
                            )}
                          </span>
                        )}
                      </td>
                      <td>
                        {editId === item.orderTypeID ? (
                          <Form.Control
                            type="text"
                            value={editMaximumCapacity}
                            onChange={(e) => setEditMaximumCapacity(e.target.value)}
                          />
                        ) : (
                          item.maximumCapacity || "-"
                        )}
                      </td>
                      <td>
                        {editId === item.orderTypeID ? (
                          <Form.Control
                            type="text"
                            value={editAcceptanceTiming}
                            onChange={(e) => setEditAcceptanceTiming(e.target.value)}
                          />
                        ) : (
                          item.acceptanceTiming || "-"
                        )}
                      </td>
                      <td>
                        {editId === item.orderTypeID ? (
                          <Form.Check
                            type="checkbox"
                            checked={editActive}
                            onChange={(e) => setEditActive(e.target.checked)}
                            label={editActive ? "Active" : "Inactive"}
                          />
                        ) : (
                          <span
                            className={`badge ${item.isActive ? "bg-success" : "bg-danger"} cursor-pointer`}
                            onClick={() => toggleStatus(item.orderTypeID, item.isActive)}
                            style={{ cursor: "pointer" }}
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </span>
                        )}
                      </td>
                      <td>
                        {editId === item.orderTypeID ? (
                          <div className="d-flex justify-content-center gap-2">
                            <div 
                              style={{ 
                                opacity: updating ? 0.6 : 1,
                                cursor: updating ? 'not-allowed' : 'pointer',
                                display: 'inline-block'
                              }}
                            >
                                
                         <div className="action-btn delete custom-edit-icon"
                        
                                onClick={handleUpdateClick}
                              >
                                <i className="bi bi-check-lg" title="Update"></i> {updating ? "Updating..." : ""}
                              </div>
                            </div>
                             
                         <div className="action-btn delete custom-edit-icon"
                            onClick={cancelEdit}>
                              <i className="bi bi-x-lg" title="Cancel"></i>
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex justify-content-center gap-2">
                           
                         <div className="action-btn delete custom-edit-icon"
                        
                              onClick={() => handleEdit(item)}
                            >
                              <i className="bi bi-pencil-square" title="Edit"></i> 
                            </div>
                              
                         <div className="action-btn delete custom-edit-icon"
                        
                              onClick={() => confirmDelete(item.orderTypeID)}
                            >
                              <i className="bi bi-trash" title="Delete"></i> 
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
     
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        dialogClassName="custom-top-modal"
      >
        <Modal.Body className="text-center">
          <p className="fs-6">Are you sure you want to delete this order type?</p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
         <button type="button" className="btn py-2 px-3 btn-primary" onClick={handleDelete}>
            Yes
          </button>
             <button type="button" className="btn py-2 px-3 btn-secondary mx-2" onClick={() => setShowDeleteModal(false)}>
          No
          </button>
        </Modal.Footer>
      </Modal>
      <style>
        {`
          .disabled-btn {
            opacity: 0.6;
            cursor: not-allowed !important;
          }
          
          .disabled-btn button {
            pointer-events: none;
          }
        `}
      </style>
    </div>
  );
};

export default AddOrderType;